import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Building2 } from "lucide-react";
import { departmentService } from "../api";
import { DepartmentForm } from "../components/DepartmentForm";
import { PageLoader } from "#/components/common/PageLoader";
import type { DepartmentCreateRequest, DepartmentResponse } from "../types";

export default function DepartmentEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [department, setDepartment] = useState<DepartmentResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    departmentService.getById(Number(id))
      .then(setDepartment)
      .catch((e) => setError(e?.response?.data?.message ?? "Failed to load."))
      .finally(() => setLoading(false));
  }, [id]);

  async function handleSubmit(payload: DepartmentCreateRequest) {
    if (!id) return;
    setBusy(true);
    setError(null);
    try {
      await departmentService.update(Number(id), payload);
      navigate("/admin/departments");
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e?.message ?? "Failed to update department.");
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <PageLoader label="Loading department..." />;

  return (
    <div className="mx-auto max-w-2xl space-y-6 p-6">
      <Link
        to="/admin/departments"
        className="inline-flex items-center gap-2 text-sm font-semibold text-emerald-700 hover:underline"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Departments
      </Link>

      <div className="flex items-center gap-3">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-700">
          <Building2 className="h-5 w-5" />
        </div>
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">Edit Department</h1>
          <p className="text-xs text-slate-500">Head-of-department is managed on the list page.</p>
        </div>
      </div>

      {error && (
        <div className="rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700">
          <strong>Error:</strong> {error}
        </div>
      )}

      <div className="rounded-2xl border bg-white p-6 shadow-sm">
        <DepartmentForm initialValue={department} busy={busy} onSubmit={handleSubmit} />
      </div>
    </div>
  );
}
